/**
 * Support Modal Component - For users to report issues with screenshot
 * Refactored to use the new standardized Modal component
 */
import React, { useState, useCallback } from 'react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { Modal } from './modal';
import { Button } from './button';
import { Input } from './input';
import { Textarea } from './textarea';
import { Label } from './label';
import { Badge } from './badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import { 
  HelpCircle, 
  Camera, 
  X, 
  Send, 
  Loader2,
  CheckCircle,
  ImageIcon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supportAPI } from '../../lib/api';

const CATEGORIES = [
  { value: 'bug', label: 'Bug / Erro no sistema' },
  { value: 'feature', label: 'Sugestão de melhoria' },
  { value: 'question', label: 'Dúvida de uso' },
  { value: 'billing', label: 'Financeiro / Cobrança' },
  { value: 'other', label: 'Outro' },
];

export function SupportModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    subject: '',
    description: '',
  });

  const captureScreenshot = useCallback(async () => {
    try {
      setCapturing(true);
      // Close modal temporarily for clean screenshot
      onOpenChange(false);
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scale: 0.5, // Reduce size
        logging: false,
      });
      
      const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
      setScreenshot(dataUrl);
      
      // Reopen modal
      onOpenChange(true);
      toast.success('Screenshot capturado!');
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      toast.error('Erro ao capturar screenshot');
      onOpenChange(true);
    } finally {
      setCapturing(false);
    }
  }, [onOpenChange]);

  const removeScreenshot = () => {
    setScreenshot(null);
  };

  const handleSubmit = async () => {
    if (!formData.category || !formData.subject || !formData.description) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      setLoading(true);
      
      const ticketData = {
        category: formData.category,
        subject: formData.subject,
        description: formData.description,
        screenshot: screenshot || null,
        user_name: user?.name,
        user_email: user?.email,
        user_role: user?.role,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
      };

      await supportAPI.createTicket(ticketData);
      
      setSubmitted(true);
      toast.success('Ticket enviado com sucesso!');
      
      // Reset form after delay
      setTimeout(() => {
        setFormData({ category: '', subject: '', description: '' });
        setScreenshot(null);
        setSubmitted(false);
        onOpenChange(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting ticket:', error);
      toast.error('Erro ao enviar ticket. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ category: '', subject: '', description: '' });
      setScreenshot(null);
      setSubmitted(false);
      onOpenChange(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <Modal
        open={open}
        onOpenChange={handleClose}
        title="Ticket Enviado"
        size="sm"
        footer={null}
      >
        <div className="py-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-emerald-500" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Ticket Enviado!</h3>
          <p className="text-muted-foreground">
            Nossa equipe irá analisar sua solicitação em breve.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      title={
        <span className="flex items-center gap-2">
          <HelpCircle className="h-5 w-5 text-primary" />
          Suporte
        </span>
      }
      description="Descreva sua dúvida ou problema. Você pode anexar um screenshot da tela."
      size="default"
      onConfirm={handleSubmit}
      onCancel={handleClose}
      confirmText={loading ? 'Enviando...' : 'Enviar Ticket'}
      cancelText="Cancelar"
      loading={loading}
      confirmIcon={loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
    >
      <div className="space-y-4">
        {/* Category */}
        <div className="space-y-2">
          <Label>Categoria *</Label>
          <Select
            value={formData.category}
            onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}
          >
            <SelectTrigger data-testid="support-category">
              <SelectValue placeholder="Selecione uma categoria" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Subject */}
        <div className="space-y-2">
          <Label>Assunto *</Label>
          <Input
            placeholder="Resumo do problema ou dúvida"
            value={formData.subject}
            onChange={(e) => setFormData(p => ({ ...p, subject: e.target.value }))}
            data-testid="support-subject"
          />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label>Descrição *</Label>
          <Textarea
            placeholder="Descreva detalhadamente o problema, dúvida ou sugestão..."
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
            data-testid="support-description"
          />
        </div>

        {/* Screenshot Section */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Camera className="h-4 w-4" />
            Screenshot (opcional)
          </Label>
          
          {screenshot ? (
            <div className="relative border rounded-lg overflow-hidden">
              <img 
                src={screenshot} 
                alt="Screenshot" 
                className="w-full h-32 object-cover"
              />
              <div className="absolute top-2 right-2 flex gap-1">
                <Badge variant="secondary" className="text-xs">
                  <ImageIcon className="h-3 w-3 mr-1" />
                  Anexado
                </Badge>
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="h-6 w-6"
                  onClick={removeScreenshot}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="w-full h-20 border-dashed"
              onClick={captureScreenshot}
              disabled={capturing}
              data-testid="capture-screenshot-btn"
            >
              {capturing ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Capturando...
                </>
              ) : (
                <>
                  <Camera className="h-5 w-5 mr-2" />
                  Capturar Screenshot da Tela
                </>
              )}
            </Button>
          )}
          <p className="text-xs text-muted-foreground">
            O screenshot ajuda nossa equipe a entender melhor o problema.
          </p>
        </div>

        {/* User Info (auto-filled) */}
        <div className="p-3 bg-muted/50 rounded-lg text-sm">
          <p className="text-muted-foreground">
            Enviando como: <span className="font-medium text-foreground">{user?.name}</span>
            <span className="text-xs ml-2">({user?.email})</span>
          </p>
        </div>
      </div>
    </Modal>
  );
}

export default SupportModal;
